import circle from '@turf/circle'
import union from '@turf/union'
import GoogleGeoCoding from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/Map/GoogleGeoCoding'
import ReverseGeoCodingResult from '@/models/ReverseGeoCodingResult'
import { POISearchRepository } from '@/models/POI/POISearchRepository'
import { locationGroupType as LocationGroupType } from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/constants'
export class MenuSetting {
  constructor(menuSetting) {
    const {
      id,
      targetCode,
      enableBulkSetting,
      radius,
      minutesAgo,
      visitCount,
      simulationCustomLocationGroups,
      simulationPoiLocationGroups,
    } = menuSetting
    this.id = id
    this.targetCode = targetCode
    this.enableBulkSetting = enableBulkSetting
    this.radius = radius
    this.minutesAgo = minutesAgo
    this.visitCount = visitCount
    this.simulationCustomLocationGroups = new SimulationLocationGroups(
      simulationCustomLocationGroups,
      LocationGroupType.CUSTOM_LOCATION,
    )
    this.simulationPoiLocationGroups = new SimulationLocationGroups(
      simulationPoiLocationGroups,
      LocationGroupType.POI_LOCATION,
    )
  }

  static MIN_RADIUS = 100
  static MAX_RADIUS = 10000
  static MAX_GROUP_LIMIT = 99
  static MAX_SIMULATION_LOCATIONS = 249
  static VALIDATION_MESSAGES = {
    upperLimit: '登録できるグループの上限に達しました',
  }

  isUpperLimit() {
    return (
      MenuSetting.MAX_GROUP_LIMIT <=
      this.simulationCustomLocationGroups.currentCount +
        this.simulationPoiLocationGroups.currentCount
    )
  }

  isUpperSimulationLocationsLimit() {
    return (
      MenuSetting.MAX_SIMULATION_LOCATIONS <
      this.simulationCustomLocationGroups.simulationLocationsCurrentCount +
        this.simulationPoiLocationGroups.simulationLocationsCurrentCount
    )
  }

  get currentGroupCount() {
    return (
      this.simulationCustomLocationGroups.simulationLocationGroups.length +
      this.simulationPoiLocationGroups.simulationLocationGroups.length
    )
  }

  get simulationLocationsCurrentCount() {
    return (
      this.simulationCustomLocationGroups.activeSimulationLocationsCount() +
      this.simulationPoiLocationGroups.activeSimulationLocationsCount()
    )
  }

  get simulationLocationsAllCount() {
    return (
      this.simulationCustomLocationGroups.allSimulationLocationsCount() +
      this.simulationPoiLocationGroups.allSimulationLocationsCount()
    )
  }

  updateRadius(radius) {
    this.radius = radius
    this.simulationCustomLocationGroups.updateRadiusAll(radius)
    this.simulationPoiLocationGroups.updateRadiusAll(radius)
  }

  setEnableBulkSetting(value) {
    if (value) {
      const radius =
        this.simulationPoiLocationGroups.simulationLocationGroups.length === 0
          ? 1000
          : 300
      this.radius = radius
      this.simulationCustomLocationGroups.updateRadiusAll(radius)
      this.simulationPoiLocationGroups.updateRadiusAll(radius)
    } else {
      this.simulationPoiLocationGroups
        .getSimulatableGroups()
        .forEach(group => (group.radius = this.radius))
      const radius =
        this.simulationPoiLocationGroups.simulationLocationGroups.length === 0
          ? 1000
          : 300
      this.radius = radius
    }
    this.enableBulkSetting = value
  }

  setTargetCode(target) {
    this.targetCode = target
  }

  get isUnderMinRadius() {
    if (!this.enableBulkSetting) {
      const customLocationGroups = this.simulationCustomLocationGroups.getGroups()
      const poiLocationGroups = this.simulationPoiLocationGroups.getGroups()
      return (
        customLocationGroups.some(
          group => group.radius < MenuSetting.MIN_RADIUS,
        ) ||
        poiLocationGroups.some(group => group.radius < MenuSetting.MIN_RADIUS)
      )
    }
    return this.radius < MenuSetting.MIN_RADIUS
  }

  get isAboveMaxRadius() {
    const customLocationGroups = this.simulationCustomLocationGroups.getGroups()
    const poiLocationGroups = this.simulationPoiLocationGroups.getGroups()
    return (
      customLocationGroups.some(
        group => group.radius > MenuSetting.MAX_RADIUS,
      ) ||
      poiLocationGroups.some(group => group.radius > MenuSetting.MAX_RADIUS)
    )
  }

  get isRadiusNotSetting() {
    const customLocationGroups = this.simulationCustomLocationGroups.getGroups()
    const poiLocationGroups = this.simulationPoiLocationGroups.getGroups()
    return (
      customLocationGroups.some(group => group.radius === undefined) ||
      poiLocationGroups.some(group => group.radius === undefined)
    )
  }

  get isLocationSettingComplete() {
    if (this.isRadiusNotSetting) return false
    if (this.isAboveMaxRadius) return false
    if (this.isUnderMinRadius) return false
    if (
      this.simulationCustomLocationGroups.isSimulatableGroupEmpty &&
      this.simulationPoiLocationGroups.isSimulatableGroupEmpty
    )
      return false
    return true
  }

  get isSimulatable() {
    if (this.isRadiusNotSetting) return false
    if (this.isAboveMaxRadius) return false
    if (this.isUnderMinRadius) return false
    if (
      this.simulationCustomLocationGroups.isSimulatableGroupEmpty &&
      this.simulationPoiLocationGroups.isSimulatableGroupEmpty
    )
      return false
    if (this.isUpperSimulationLocationsLimit()) return false
    return true
  }

  get validations() {
    return {
      advanced: {
        required: value =>
          Boolean(value !== undefined && value !== '') || '入力して下さい',
        overZero: value => Number(value) >= 0 || '0以上の数値を入力して下さい',
        overOne: value => Number(value) >= 1 || '1以上の数値を入力して下さい',
        overHundred: value =>
          Number(value) >= 100 || '100以上の数値を入力して下さい',
        integer: value =>
          Number.isInteger(Number(value)) || '整数で入力して下さい',
        withinOneYear: value =>
          Number(value) <= 365 || '365日よりも前は指定できません',
      },
      radiusSetting: {
        min: value =>
          Number(value) >= MenuSetting.MIN_RADIUS ||
          `${MenuSetting.MIN_RADIUS}以上の数値を入力して下さい`,
        max: value =>
          Number(value) <= MenuSetting.MAX_RADIUS ||
          `${MenuSetting.MAX_RADIUS}以下の数値を入力して下さい`,
        required: value =>
          Boolean(value !== undefined && value !== '') || '入力して下さい',
      },
    }
  }
}

export class SimulationLocationGroups {
  get hasSimulatableGroup() {
    return this.getSimulatableGroups().length > 0
  }

  constructor(simulationLocationGroups, locationGroupType) {
    this.type = locationGroupType
    switch (this.type) {
      case LocationGroupType.CUSTOM_LOCATION:
        this.simulationLocationGroups = simulationLocationGroups
          .map(s => new SimulationCustomLocationGroup(s))
          .sort(this.compareById)
        break

      case LocationGroupType.POI_LOCATION:
        this.simulationLocationGroups = simulationLocationGroups
          .map(s => new SimulationPoiLocationGroup(s))
          .sort(this.compareById)
        break
    }
  }

  compareById(a, b) {
    if (a.id > b.id) {
      return 1
    }
    if (a.id < b.id) {
      return -1
    }
    return 0
  }

  getGroups() {
    return this.simulationLocationGroups
  }

  getGroup(id) {
    const ret = this.simulationLocationGroups.find(g => g.id === id)
    if (ret === undefined) return false
    return ret
  }

  copyGroup(baseGroupId, newGroupName) {
    const baseGroup = this.getGroup(baseGroupId)

    const simulationLocations = baseGroup.simulationLocations.map(
      (location, i) => ({
        ...location,
        id: makeTemporaryId(i),
      }),
    )

    const newGroup = {
      ...baseGroup,
      id: makeTemporaryId(),
      name: newGroupName,
      simulationLocations,
    }

    this.addGroup(newGroup)
  }

  updateRadiusAll(radius) {
    this.simulationLocationGroups.map(g => g.updateRadius(radius))
  }

  removeLocationGroup(id) {
    this.simulationLocationGroups = this.simulationLocationGroups.filter(
      g => g.id !== id,
    )
  }

  getSimulatableGroups() {
    return this.activeGroups
      .filter(group => group.hasActiveLocation)
      .map(group => {
        return {
          ...group,
          id: group.isNew() ? undefined : group.id,
          simulationLocations: group.getActiveLocations(),
        }
      })
  }

  async addGroup(locationGroup) {
    switch (this.type) {
      case LocationGroupType.CUSTOM_LOCATION: {
        const newGroup = SimulationCustomLocationGroup.assign(locationGroup)
        this.simulationLocationGroups.push(newGroup)
        break
      }
      case LocationGroupType.POI_LOCATION: {
        const newGroup = SimulationPoiLocationGroup.assign(locationGroup)
        await newGroup.fetchSimulationPoiLocations()
        this.simulationLocationGroups.push(newGroup)
        break
      }
    }
  }

  async addManyGroup(locationGroups) {
    switch (this.type) {
      case LocationGroupType.CUSTOM_LOCATION: {
        const newGroups = locationGroups.map(locationGroup =>
          SimulationCustomLocationGroup.assign(locationGroup),
        )
        this.simulationLocationGroups = [
          ...this.simulationLocationGroups,
          ...newGroups,
        ]
        break
      }
      // 現在使われていない？
      case LocationGroupType.POI_LOCATION: {
        const newGroups = locationGroups.map(locationGroup =>
          SimulationPoiLocationGroup.assign(locationGroup),
        )
        this.simulationLocationGroups = [
          ...this.simulationLocationGroups,
          ...newGroups,
        ]
        break
      }
    }
  }

  get activeGroups() {
    return this.simulationLocationGroups.filter(group => group.isActive())
  }

  get isSimulatableGroupEmpty() {
    return this.getSimulatableGroups().length === 0
  }

  get currentCount() {
    return this.simulationLocationGroups.length
  }

  get simulationLocationsCurrentCount() {
    return this.activeSimulationLocationsCount()
  }

  get SimulationLocationsAllCount() {
    return this.allSimulationLocationsCount()
  }

  allSimulationLocationsCount() {
    return this.simulationLocationGroups
      .map(group => group.simulationLocations.length)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
  }

  activeSimulationLocationsCount() {
    let count = 0
    this.getSimulatableGroups()
      .map(group => group.simulationLocations)
      .forEach(simulationLocations => (count += simulationLocations.length))
    return count
  }
}

// SimulationCustomLocationGroup,SimulationPoiLocationGroupの継承元になる抽象的なクラス
export class SimulationLocationGroup {
  constructor(simulationLocationGroup) {
    const {
      id,
      name,
      radius,
      status = 'active',
      added = false,
    } = simulationLocationGroup
    this.id = id
    this.name = name
    this.radius = radius
    this.status = status
    this.added = added
  }

  static MAX_NAME_LENGTH = 255

  static get validations() {
    return {
      max: value => {
        const max = SimulationLocationGroup.MAX_NAME_LENGTH
        return (
          max >= value.length ||
          `地点グループは${max}文字以内で入力してください`
        )
      },
      required: value =>
        Boolean(value !== undefined && value !== '') || '入力して下さい',
    }
  }

  get hasLocations() {
    return !!this.simulationLocations.length > 0
  }

  get isEmptyRadius() {
    return !this.radius
  }

  get type() {
    return LocationGroupType.CUSTOM_LOCATION
  }

  isActive() {
    return this.status === 'active'
  }

  updateStatus(value) {
    this.status = value ? 'active' : 'inactive'
  }

  updateRadius(radius) {
    this.radius = radius
  }

  removeLocationPoint(id) {
    this.simulationLocations = this.simulationLocations.filter(
      location => location.id !== id,
    )
  }

  addLocation(location) {
    this.simulationLocations = [...this.simulationLocations, location]
  }

  addManyLocation(locations) {
    this.simulationLocations = [...this.simulationLocations, ...locations]
  }

  isNew() {
    return this.added
  }

  getActiveLocations() {
    return this.simulationLocations
      .filter(location => location.isActive())
      .map(location => {
        return {
          ...location,
          id: location.isNew() ? undefined : location.id,
        }
      })
  }

  get hasActiveLocation() {
    return this.simulationLocations.some(location => location.isActive())
  }
}

export class SimulationCustomLocationGroup extends SimulationLocationGroup {
  constructor(simulationLocationGroup) {
    super(simulationLocationGroup)
    const { simulationLocations = [] } = simulationLocationGroup
    this.simulationLocations = simulationLocations.map(
      s => new SimulationLocation(s),
    )
  }

  static DEFAULT_RADIUS = 1000

  static assign(data) {
    return new SimulationCustomLocationGroup(data)
  }

  get type() {
    return LocationGroupType.CUSTOM_LOCATION
  }
}

export class SimulationPoiLocationGroup extends SimulationLocationGroup {
  constructor(simulationLocationGroup) {
    super(simulationLocationGroup)
    const {
      administrativeDistrictType,
      area,
      categoryId,
      conditions = [],
    } = simulationLocationGroup
    this.administrativeDistrictType = administrativeDistrictType
    this.area = area
    this.categoryId = categoryId
    this.conditions = conditions
    this.simulationLocations = []

    this.fetchSimulationPoiLocations()
  }

  static DEFAULT_RADIUS = 300

  static assign(data) {
    return new SimulationPoiLocationGroup(data)
  }

  get type() {
    return LocationGroupType.POI_LOCATION
  }

  async updatePoiConditions(poiSearchForm) {
    const {
      administrativeDistrictType,
      area,
      categoryId,
      conditions = [],
    } = poiSearchForm
    this.administrativeDistrictType = administrativeDistrictType
    this.area = area
    this.categoryId = categoryId
    this.conditions = conditions
    this.simulationLocations = []

    await this.fetchSimulationPoiLocations()
  }

  isPoiSearchConditionsEmpty() {
    if (this.conditions.length === 0) return true
    return (
      this.conditions.length === 1 &&
      this.conditions[0].poiSubCategoryId === null
    )
  }

  async fetchSimulationPoiLocations() {
    const simulationLocations = await POISearchRepository.getPoiLocationList(
      this.categoryId,
      this.isPoiSearchConditionsEmpty() ? [] : this.conditions,
      this.administrativeDistrictType,
      this.area,
    )

    this.simulationLocations = simulationLocations.map(s => {
      // poi locationのデータのcustom locationのデータフォーマットに合わせる
      const poiLocationData = {
        shape: {
          type: s.shapeType.charAt(0).toUpperCase() + s.shapeType.slice(1), //先頭は大文字にする
          coordinates: [s.shape.longitude, s.shape.latitude],
        },
      }
      return new SimulationLocation(poiLocationData)
    })
  }
}

export class SimulationLocation {
  constructor(simulationLocation) {
    // poi locationの場合、name,statusを持たない
    const {
      id = null,
      name = null,
      shape,
      status = 'active',
      added = false,
    } = simulationLocation
    this.id = id
    this.name = name ? name : 'poi_location'
    this.updated = false
    this.shapeType = shape.type.toLowerCase()
    this.shape = shapeFactory(shape) // type, coordinate
    this.status = status
    this.added = added
  }

  static async assign(data) {
    return new SimulationLocation(data)
  }

  static async assignByLngLat(lng, lat) {
    const { results } = await GoogleGeoCoding.reverseGeoCoding({ lng, lat })
    const { pointName } = new ReverseGeoCodingResult(results)
    const location = this.makeLocationData({ name: pointName, lng, lat })
    return new SimulationLocation(location)
  }

  static async assignByAddress(address) {
    const { results } = await GoogleGeoCoding.geoCoding(address)
    const [res] = results
    const { lat, lng } = res.geometry.location
    const location = this.makeLocationData({ name: address, lng, lat })
    return new SimulationLocation(location)
  }

  static makeLocationData(data) {
    const { id, name, lng, lat } = data
    return {
      id: id ? id : makeTemporaryId(),
      name,
      status: 'active',
      shapeType: 'point',
      shape: {
        type: 'Point',
        coordinates: [lng, lat],
      },
      added: true,
    }
  }

  updateStatus(value) {
    this.status = value ? 'active' : 'inactive'
  }

  update() {
    this.updated = true
  }
  isNew() {
    return this.added
  }
  isActive() {
    if (this.status === 'active') return true
  }
  isUpdated() {
    return this.updated
  }
}

const shapeFactory = shape => {
  const type = shape.type
  if (type === 'Point') return new Point(shape)
}

// Point, Line, PoligonはすべてgetCooridinatesというインターフェースを持つ
class Point {
  constructor(shape) {
    const { type, coordinates } = shape
    this.type = type
    this.coordinates = coordinates
  }
  getCoordinates() {
    const lng = parseFloat(this.coordinates[0])
    const lat = parseFloat(this.coordinates[1])
    return [lng, lat]
  }
  setLngLat({ lng, lat }) {
    this.coordinates = [lng, lat]
  }
}

// class Line {
//   constructor(shape) {
//     const { type, coordinates } = shape
//     this.type = type
//     this.coordinates = coordinates
//   }
//   getCoordinates() {
//     return this.coordinates
//   }
// }

export class SimulationFormDelivery {
  constructor({ menuSetting }) {
    this.menuSetting = new MenuSetting(menuSetting)
  }
}

export const metersToKilometers = radius => radius / 1000

// 位置情報と半径を受け取って、geojsonを作る
export const pointsToGeoJSON = (points, radius) => {
  const radiusKM = metersToKilometers(radius)
  const polygons = points.map(point => {
    const polygon = generateCirclePolygon(
      point.shape.getCoordinates(),
      radiusKM,
    )
    return polygon
  })
  // polygon同士をunionし、境界をなくす
  const feature = polygons.reduce((accumulatePolygon, currentPolygon) => {
    return union(accumulatePolygon, currentPolygon)
  })
  const geoJson = {
    type: 'FeatureCollection',
    features: [feature],
  }
  return geoJson
}

// 中心座標と半径から、ポリゴンを作る
const generateCirclePolygon = (center, radius) => {
  const options = {
    steps: 64,
    units: 'kilometers',
  }
  const circlePolygon = circle(center, radius, options)
  return circlePolygon
}

/**
 * フロントのみで利用する一時的なIDをtimestampを用いて生成する
 */
export const makeTemporaryId = (index = undefined) => {
  const tempId = new Date().getTime()
  return typeof index === 'undefined' ? tempId : tempId + index
}
